import { useState } from 'react'
import type { SessionCommand } from '@fitting-room/contracts'
import { StepShell } from '../../components/RoomChrome'
import { uiStrings } from '../../lib/strings'
import { text } from '../../lib/text'
import type { StepProps } from '../types'
import { usePendingExit } from '../usePendingExit'

/**
 * Her usual dress size, from the boutique's own size chart.
 *
 * The sizes are the chart's, in the chart's order, and arrive on the view as
 * words and nothing else: which sample sizes each one fits is the shop's
 * business and is applied on the server. Nothing here knows that a size is a
 * number, or that bridal sizing runs large.
 *
 * One tap chooses and moves on. It is a single answer from a short list, and
 * a Continue button after it would be a second tap for nothing — the same
 * reason the catalogue screen before it has none.
 */
export function SizeStep({ step, ctx }: StepProps<'size'>) {
  const { view, locale } = ctx
  const t = uiStrings(locale)
  const sizes = view.tenant.sizes
  const [chosen, setChosen] = useState<string | null>(view.session.answers.size ?? null)

  const choose = (size: string | null) => {
    setChosen(size)
    const command: SessionCommand = { type: 'set_size', size }
    ctx.advance([command])
  }

  // Leaving by "Back to browsing" carries the size she tapped, as Continue would.
  usePendingExit(ctx, chosen !== (view.session.answers.size ?? null) ? () => [{ type: 'set_size', size: chosen }] : null)

  return (
    <StepShell
      title={text(step.title, locale)}
      body={text(step.body, locale)}
      progress={ctx.progress}
      locale={locale}
      skip={step.skippable ? { label: t.skipThis, disabled: ctx.pending, onSkip: () => ctx.advance() } : undefined}
    >
      {/*
        A chart nobody has filled in has nothing to offer, and asking anyway
        would be a screen of nothing with a skip at the top. The way on is the
        only thing worth drawing.
      */}
      {sizes.length === 0 ? (
        <button type="button" className="fr-btn fr-btn-primary w-full" disabled={ctx.pending} onClick={() => ctx.advance()}>
          {t.continue}
        </button>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-2" role="radiogroup" aria-label={text(step.title, locale) || step.id}>
            {sizes.map((size) => {
              const selected = chosen === size
              return (
                <button
                  key={size}
                  type="button"
                  className="fr-tile"
                  role="radio"
                  aria-checked={selected}
                  data-selected={selected}
                  disabled={ctx.pending}
                  style={{ minHeight: '3.5rem', padding: '0.75rem 0.25rem' }}
                  onClick={() => choose(size)}
                >
                  {/* The UI face, not the display one: its old-style figures set a 10 as "IO". */}
                  <span className="text-[1.125rem] leading-none tabular-nums" style={{ letterSpacing: '0.04em' }}>
                    {size}
                  </span>
                </button>
              )
            })}
          </div>
          <button
            type="button"
            className="fr-choice-row mt-4 w-full"
            data-optout
            disabled={ctx.pending}
            onClick={() => choose(null)}
          >
            <span className="fr-display text-[1.125rem] leading-tight">{text(step.notSureLabel, locale) || t.notSure}</span>
          </button>
        </>
      )}
    </StepShell>
  )
}
